// Bug vocabulary: severity badges (solid for S1/S2, soft for S3/S4), priority, status, resolution.
import { useTranslation } from "react-i18next";
import type { BugPriority } from "@/api/types/BugPriority";
import type { BugStatus } from "@/api/types/BugStatus";
import type { Resolution } from "@/api/types/Resolution";
import type { Severity } from "@/api/types/Severity";
import { BUG_STATUSES, formatMinutes, minutesToDue } from "@/lib/bugs";
import { Chip, cx } from "./ui";

const SEVERITY_CLASS: Record<Severity, string> = {
  s1: "bg-[#b42318] text-white",
  s2: "bg-[#c4461a] text-white",
  s3: "bg-blocked-bg text-blocked-fg",
  s4: "bg-skip-bg text-skip-fg",
};

export function SeverityBadge({ severity, long }: { severity: Severity; long?: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      title={t(`severity.${severity}`)}
      className={cx(
        "inline-flex items-center rounded-[5px] px-[7px] font-mono text-xs leading-5 font-bold whitespace-nowrap",
        SEVERITY_CLASS[severity],
      )}
    >
      {long ? t(`severity.${severity}`) : t(`severity.short.${severity}`)}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: BugPriority }) {
  const { t } = useTranslation();
  return <Chip tone={priority === "p0" ? "fail" : "neutral"}>{t(`bugPriority.${priority}`)}</Chip>;
}

export function BugStatusChip({ status, resolution }: { status: BugStatus; resolution?: Resolution | null }) {
  const { t } = useTranslation();
  if (status === "closed") {
    return (
      <Chip tone={resolution && resolution !== "fixed" ? "skip" : "pass"} icon="check">
        {resolution ? t("bug.closedAs", { resolution: t(`resolution.${resolution}`) }) : t("bugStatus.closed")}
      </Chip>
    );
  }
  const tone = status === "reported" ? "fail" : status === "fixed" || status === "verified" ? "pass" : "accent";
  return <Chip tone={tone}>{t(`bugStatus.${status}`)}</Chip>;
}

/** The lifecycle as pills: done ✓, current filled, later grey. */
/** Where the bug is in its lifecycle: the current status, a thin segmented bar and what comes next. */
export function StatusProgress({ status }: { status: BugStatus }) {
  const { t } = useTranslation();
  const current = BUG_STATUSES.indexOf(status);
  const next = BUG_STATUSES[current + 1];
  return (
    <div className="flex max-w-xl flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
        <span className="font-semibold">{t(`bugStatus.${status}`)}</span>
        <span className="text-xs text-muted">
          {t("bug.stepOf", { n: current + 1, total: BUG_STATUSES.length })}
          {next && ` · ${t("bug.nextStep", { status: t(`bugStatus.${next}`) })}`}
        </span>
      </div>
      <ol aria-label={t("bugs.filterStatus")} className="m-0 flex list-none gap-1 p-0">
        {BUG_STATUSES.map((s, i) => (
          <li
            key={s}
            title={t(`bugStatus.${s}`)}
            aria-current={i === current ? "step" : undefined}
            className={cx("h-1.5 flex-1 rounded-full", i < current ? "bg-pass-fg/70" : i === current ? "bg-accent" : "bg-surface-2")}
          >
            <span className="sr-only">
              {t(`bugStatus.${s}`)}
              {i < current ? " ✓" : ""}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Mitigation target from the severity: red once overdue, a quiet countdown in the last 24 hours, nothing otherwise.
 */
export function DueBadge({ dueAt }: { dueAt: string | null }) {
  const { t } = useTranslation();
  const left = minutesToDue(dueAt);
  if (left == null || left > 24 * 60) return null;
  return left < 0 ? (
    <Chip tone="fail" icon="clock">
      {t("bugs.overdue", { time: formatMinutes(-left) })}
    </Chip>
  ) : (
    <Chip tone="blocked" icon="clock">
      {t("bugs.dueIn", { time: formatMinutes(left) })}
    </Chip>
  );
}
